import { useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { acknowledgeEligibility } from "@/features/oic/api";
import type { OICFlow, OICSummary } from "@/features/oic/types";

interface Props {
  clientId: string;
  caseId: string;
  flow: OICFlow;
  summary: OICSummary | null;
  onFlow: (flow: OICFlow) => void;
  onContinue: () => void;
}

/**
 * Step 3 — Eligibility gate. Surfaces the §9 gates from the summary:
 *  - expired CSED → the "No Good" dead-end (hard block: Terms/Packet locked);
 *  - missing CSED → non-blocking 10-year-assumption warning;
 *  - $0 tax due → non-blocking warning;
 *  - full-pay compare → soft "consider an IA instead" warning (proceed w/ override).
 * The practitioner acknowledges to advance (records `eligibility_ack`/note/override).
 */
export function EligibilityStep({ clientId, caseId, flow, summary, onFlow, onContinue }: Props) {
  const [ack, setAck] = useState(flow.eligibilityAck);
  const [note, setNote] = useState(flow.eligibilityNote);
  const [override, setOverride] = useState(flow.fullPayOverride);
  const [saving, setSaving] = useState(false);

  const expired = summary?.expired ?? flow.status === "INELIGIBLE_CSED";
  const fullPay = summary?.fullPayPossible ?? false;

  // --- No-Good dead-end (expired CSED) ---
  if (expired) {
    const years = summary?.expiredCseds ?? [];
    return (
      <Alert variant="destructive">
        <AlertTitle>Offer not appropriate — collection statute expired</AlertTitle>
        <AlertDescription className="grid gap-3">
          <p>
            The collection statute has expired for tax year(s){" "}
            <span className="font-medium">{years.join(", ") || "on this case"}</span>. The IRS
            can no longer collect this debt — it should already be written off, so an offer
            does not apply. This flow cannot advance to Terms or Packet.
          </p>
          <Button asChild variant="outline" size="sm" className="w-fit">
            <Link to={`/client/${clientId}/cases/${caseId}/transcripts/tax-owed`}>
              <FiExternalLink className="size-4" /> Review transcripts &amp; CSED
            </Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  async function acknowledge() {
    setSaving(true);
    try {
      const updated = await acknowledgeEligibility(caseId, {
        eligibilityAck: ack,
        eligibilityNote: note,
        fullPayOverride: override,
        step: "terms",
      });
      onFlow(updated);
      onContinue();
    } catch {
      toast.error("Couldn't save the eligibility acknowledgement.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-4">
      {summary?.warnings?.length ? (
        <div className="grid gap-2">
          {summary.warnings.map((w) => (
            <Alert key={w} variant={fullPay ? "default" : "default"}>
              <AlertDescription>{w}</AlertDescription>
            </Alert>
          ))}
        </div>
      ) : (
        <Alert>
          <AlertTitle>No blocking issues</AlertTitle>
          <AlertDescription>
            No expired statute or $0-due problems detected. Acknowledge to continue.
          </AlertDescription>
        </Alert>
      )}

      {fullPay && (
        <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
          <Checkbox
            checked={override}
            onCheckedChange={(v) => setOverride(v === true)}
            className="mt-0.5"
          />
          <span>
            I understand the client can likely full-pay within the statute and the IRS will
            most likely deny the offer, but I'm choosing to proceed.
          </span>
        </label>
      )}

      <div className="grid gap-2">
        <Label htmlFor="elig-note">Notes (optional)</Label>
        <Textarea
          id="elig-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Record the eligibility rationale for the file."
        />
      </div>

      <label className="flex items-center gap-3 text-sm">
        <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} />
        I've reviewed eligibility and want to proceed with an Offer in Compromise.
      </label>

      <div>
        <Button onClick={acknowledge} disabled={!ack || saving}>
          Acknowledge &amp; continue
        </Button>
      </div>
    </div>
  );
}
